import { useCallback, useEffect, useMemo } from 'react';
import { useClickOutside } from '@sdk/common/hooks/useClickOutside';
import { useToggle } from '@sdk/common/hooks/useToggle';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import { useSession } from '@sdk/session/SessionContext';

import { MessagePost } from '@web/conversations/types/Message';
import { Emoji } from '@web/conversations/types/Reactions';
import {
  useConversationActions,
  useConversationState,
} from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useAddReactionAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useAddReactionAction';
import { useRemoveReactionAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useRemoveReactionAction';
import { canApplyReaction } from '@web/conversations/Reactions/utils/canApplyReaction';

import { createBadges } from './messageReactions.utils';

export const useReactions = (messageId: ID) => {
  const { currentUser } = useSession();
  const { personasDict: personas } = usePersonaCollection();
  const { action: addReaction } = useAddReactionAction();
  const { action: removeReaction } = useRemoveReactionAction();
  const {
    messages: { byId },
  } = useConversationState();
  const { conversationId, reactions } = byId[messageId] as MessagePost;

  const canReact = canApplyReaction({ currentUserId: currentUser.id, reactions });

  const badges = useMemo(
    () => createBadges({ currentUser, personas, reactions }),
    [currentUser, personas, reactions]
  );

  return {
    badges,
    canReact,
    onSelect: (emoji: Emoji, isActive?: boolean) => {
      if (!canReact && !isActive) return;

      const action = isActive ? removeReaction : addReaction;

      action({
        conversationId: conversationId,
        emoji,
        messageId,
      });
    },
  };
};

export type PickerProps = {
  onSelect: (reaction: Emoji) => void;
};
export const usePicker = ({ onSelect }: PickerProps) => {
  const { lockScroll, unlockScroll } = useConversationActions();
  const { isOn, setOff, toggle } = useToggle();
  const onPick = useCallback(
    (reaction: Emoji) => {
      onSelect(reaction);
      setOff();
    },
    [onSelect, setOff]
  );
  const ref = useClickOutside<HTMLDivElement>(setOff);
  useEffect(
    () => void (isOn ? setTimeout(lockScroll) : unlockScroll()),
    [isOn, lockScroll, unlockScroll]
  );

  const position = useMemo(() => {
    if (!isOn) return;

    const rect = ref.current?.getBoundingClientRect();
    const minimumTopOffset = 320;

    const alingToBottom = rect.top < minimumTopOffset;
    const top = alingToBottom ? rect.bottom : undefined;
    const bottom = !alingToBottom ? window.innerHeight - rect.top : undefined;

    return {
      bottom,
      left: rect.left,
      top,
    };
  }, [isOn, ref]);

  return {
    handleButtonClick: toggle,
    isOpen: isOn,
    onPick,
    position,
    ref,
  };
};
